import React, { useState } from "react";
import {
  Screwdriver,
  Plus,
  X,
  CaretUp,
  CaretDown,
} from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { CompanyContactPicker } from "shared/components/contact";
import { HelpdeskTicketInterface } from "shared/types/helpdesk";
import { CompanyContactPerson } from "shared/types/contact";
import { toCompanyContactPerson } from "shared/utils/companyContact";
import { getTicketSupplierAssignments } from "../../shared/ticketSuppliers";

interface TicketSuppliersSectionProps {
  ticket: HelpdeskTicketInterface;
  onUpdate: (ticket: HelpdeskTicketInterface) => void;
  /**
   * Aangeroepen nadat een leverancier succesvol is toegevoegd (na de refetch),
   * met het ContactCompany-id van de nieuwe leverancier. De detailpagina
   * gebruikt dit om die leverancier meteen te selecteren, zodat er zonder harde
   * refresh naar gemaild kan worden.
   */
  onSupplierAdded?: (supplierId: number) => void;
  /** Wanneer true zijn toevoegen/verwijderen uitgeschakeld (geen rechten). */
  disabled?: boolean;
}

/**
 * Beheer van de leveranciers van een helpdeskticket (meerdere mogelijk).
 * Toont alle toegewezen leveranciers als chips met verwijderen/herordenen en
 * een knop om er een toe te voegen. Praat met de multi-leverancier endpoints:
 *   POST   /helpdesk/:id/suppliers                  { supplierId }
 *   DELETE /helpdesk/:id/suppliers/:assignmentId
 *   PUT    /helpdesk/:id/suppliers/:assignmentId/reorder { direction }
 */
export const TicketSuppliersSection: React.FC<TicketSuppliersSectionProps> = ({
  ticket,
  onUpdate,
  onSupplierAdded,
  disabled = false,
}) => {
  const { session } = useSession();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const assignments = getTicketSupplierAssignments(ticket);
  const ticketId = ticket.id;

  const refetchTicket = async () => {
    if (!ticketId) return;
    const res = await fetchWithAuth(
      `${API_URL}/helpdesk/ticket/${ticketId}`,
      { method: "GET" },
      session,
    );
    if (res.ok) {
      onUpdate(await res.json());
    }
  };

  const handleAddSupplier = async (supplierId: number) => {
    if (!ticketId) return;
    setBusy(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/helpdesk/${ticketId}/suppliers`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ supplierId }),
        },
        session,
      );
      if (!res.ok) throw new Error("Leverancier toevoegen mislukt");
      await refetchTicket();
      // Selecteer meteen de zojuist toegevoegde leverancier op de detailpagina,
      // zodat er zonder harde refresh naar gemaild kan worden.
      onSupplierAdded?.(supplierId);
      toast.success("Leverancier toegevoegd");
    } catch (error) {
      console.error("Error adding supplier to ticket:", error);
      toast.error("Fout bij toevoegen van leverancier");
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveSupplier = async (assignmentId: number) => {
    if (!ticketId) return;
    setBusy(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/helpdesk/${ticketId}/suppliers/${assignmentId}`,
        { method: "DELETE" },
        session,
      );
      if (!res.ok) throw new Error("Leverancier verwijderen mislukt");
      await refetchTicket();
      toast.success("Leverancier verwijderd");
    } catch (error) {
      console.error("Error removing supplier from ticket:", error);
      toast.error("Fout bij verwijderen van leverancier");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Kiest de contactpersoon bij één leverancier van dit ticket. `null` wist de
   * keuze, waarna de mail weer naar het algemene adres van de firma gaat.
   */
  const handleSetContact = async (
    assignmentId: number,
    contact: CompanyContactPerson | null,
  ) => {
    if (!ticketId) return;
    setBusy(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/helpdesk/${ticketId}/suppliers/${assignmentId}/contact`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contactId: contact?.id ?? null }),
        },
        session,
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || "Contactpersoon instellen mislukt");
      }
      await refetchTicket();
      toast.success(
        contact
          ? `Communicatie loopt nu via ${contact.name}`
          : "Communicatie loopt nu via het algemene adres van de firma",
      );
    } catch (error) {
      console.error("Error setting supplier contact person:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Fout bij instellen van contactpersoon",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleReorder = async (
    assignmentId: number,
    direction: "up" | "down",
  ) => {
    if (!ticketId) return;
    setBusy(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/helpdesk/${ticketId}/suppliers/${assignmentId}/reorder`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ direction }),
        },
        session,
      );
      if (!res.ok) throw new Error("Volgorde wijzigen mislukt");
      await refetchTicket();
    } catch (error) {
      console.error("Error reordering ticket suppliers:", error);
      toast.error("Fout bij wijzigen van volgorde");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Screwdriver className="text-neutral-secondary-mid h-4 w-4" />
        <span className="text-neutral-secondary-mid text-xs font-medium">
          Leveranciers
        </span>
      </div>

      <div className="flex flex-col items-start gap-2">
        {assignments.length === 0 && (
          <span className="text-neutral-secondary-mid text-sm">
            Geen leveranciers toegewezen
          </span>
        )}

        {assignments.map((assignment, index) => (
          <div key={assignment.id} className="flex flex-wrap items-center gap-2">
            <span className="border-neutral-primary/30 bg-neutral-primary/10 text-neutral-primary inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium">
              <Screwdriver className="h-3 w-3" />
              {assignment.supplier?.name}
              {!disabled && assignments.length > 1 && (
                <>
                  <button
                    type="button"
                    disabled={busy || index === 0}
                    onClick={() => handleReorder(assignment.id, "up")}
                    className="hover:text-moss-primary disabled:opacity-30"
                    title="Naar boven"
                  >
                    <CaretUp className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    disabled={busy || index === assignments.length - 1}
                    onClick={() => handleReorder(assignment.id, "down")}
                    className="hover:text-moss-primary disabled:opacity-30"
                    title="Naar beneden"
                  >
                    <CaretDown className="h-3 w-3" />
                  </button>
                </>
              )}
              {!disabled && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => handleRemoveSupplier(assignment.id)}
                  className="hover:text-red-600 disabled:opacity-30"
                  title="Verwijderen"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>

            {/*
              Naar wie er bij deze leverancier gemaild wordt. Zonder keuze staat
              hier het algemene adres van de firma; kies je een contactpersoon,
              dan vertrekt de mail naar die persoon.
            */}
            <CompanyContactPicker
              companyId={assignment.supplier?.id}
              companyName={assignment.supplier?.name}
              companyEmail={assignment.supplier?.email}
              value={toCompanyContactPerson(assignment.contact, assignment.supplier)}
              onChange={(contact) => handleSetContact(assignment.id, contact)}
              disabled={disabled || busy}
              compact
            />
          </div>
        ))}

        {!disabled && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => setPickerOpen(true)}
            className="h-7 gap-1 px-2 text-xs"
          >
            <Plus className="h-3 w-3" />
            Leverancier toevoegen
          </Button>
        )}
      </div>

      {pickerOpen && (
        <UniversalPickerModal
          isOpen={pickerOpen}
          mode="supplier"
          title="Leverancier toevoegen aan ticket"
          onClose={() => setPickerOpen(false)}
          onConfirm={(value: any) => {
            setPickerOpen(false);
            if (value?.id != null) {
              handleAddSupplier(Number(value.id));
            }
          }}
          config={{
            allowCreateSupplier: true,
            allowRemoval: false,
          }}
        />
      )}
    </div>
  );
};
